// H additions — Admin BI FX Rates page (platform-admin writes).
//
// Single row per year × 12 months. Editable cell = monthly EUR/HUF rate
// (HUF per 1 EUR). Forwarders see this page READ-ONLY (server enforces
// 403 on writes). Customer-tier blocked by view-tier guard upstream.
import React, { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Coins, ChevronLeft, ChevronRight, Copy, Save, Loader2, AlertCircle,
} from "lucide-react";
import axios from "axios";

import { useFxRates, useFxBulkUpsert } from "../api/biExtras";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mono = (size, color, weight, ls) => ({
  fontFamily: "'DM Mono', monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});
const syne = (size, weight, ls) => ({
  fontFamily: "Syne, sans-serif",
  fontSize: size,
  fontWeight: weight || 700,
  letterSpacing: ls,
});

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const _toNum = (v) => (v === "" || v == null ? null : Number(v));

export default function AdminBiFxRates() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isPlatformAdmin = user?.role === "platform_admin";

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());

  const { data: rates = [], isLoading, error } = useFxRates(year);
  const bulk = useFxBulkUpsert();

  // Per-month draft string state.
  const [draft, setDraft] = useState({}); // {1..12: "string"}

  useEffect(() => {
    const seed = {};
    for (const r of rates) {
      seed[r.month] = r.eur_huf_rate;
    }
    setDraft(seed);
  }, [rates, year]);

  const onSave = async () => {
    const body = { rates: [] };
    for (let m = 1; m <= 12; m++) {
      const v = _toNum(draft[m]);
      if (v == null || v <= 0) continue;
      body.rates.push({ year, month: m, eur_huf_rate: v });
    }
    if (body.rates.length === 0) {
      toast.error("Nothing to save");
      return;
    }
    try {
      const res = await bulk.mutateAsync(body);
      toast.success(`Saved · ${res.inserted} new · ${res.updated} updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Save failed");
    }
  };

  const onCopyPrev = async () => {
    if (!window.confirm(`Copy ALL ${year - 1} rates into ${year}? Existing rows will be overwritten.`)) return;
    try {
      const r = await axios.get(`${API}/bi/fx-rates?year=${year - 1}`);
      const prev = r.data || [];
      if (prev.length === 0) {
        toast.error(`No rates found for ${year - 1}`);
        return;
      }
      const body = { rates: prev.map((p) => ({ year, month: p.month, eur_huf_rate: p.eur_huf_rate })) };
      const res = await bulk.mutateAsync(body);
      toast.success(`Copied ${prev.length} rates from ${year - 1} (${res.inserted} new, ${res.updated} updated)`);
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  if (error?.response?.status === 403) {
    return (
      <div data-testid="bi-fx-rates-403" className="rounded-lg p-6" style={{ background: "rgba(220,53,69,0.06)", border: "1px solid rgba(220,53,69,0.30)" }}>
        <AlertCircle size={16} color="#ef4444" className="inline mr-2" />
        <span style={mono(12, "#ef4444", 700)}>BI FX rates is forwarder-internal only. Your account doesn't have access.</span>
      </div>
    );
  }

  return (
    <div data-testid="admin-bi-fx-rates-page">
      <header className="mb-3">
        <h1 style={syne("clamp(22px, 3vw, 28px)", 800, "-0.5px")} data-testid="view-title">
          <Coins size={20} className="inline mr-2" />
          BI · FX Rates (EUR ↔ HUF, monthly)
        </h1>
        <div className="mt-1" style={mono(11, "#94a3b8")}>
          {isPlatformAdmin ? "Platform-admin · enter HUF per 1 EUR per month. Used to convert dashboards when toggling EUR/HUF." : "Forwarder read-only view — contact your platform admin to edit"}
        </div>
      </header>

      <div className="flex items-center gap-2 mb-3" data-testid="bi-fx-rates-toolbar">
        <button onClick={() => setYear((y) => y - 1)} className="px-2.5 py-1.5 rounded" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.18)", color: "#c5cad8", cursor: "pointer" }} data-testid="bi-fx-rates-year-prev"><ChevronLeft size={14} /></button>
        <span style={{ ...syne(18, 800, "-0.3px"), color: "#9ec3ec" }} data-testid="bi-fx-rates-year-label">{year}</span>
        <button onClick={() => setYear((y) => y + 1)} className="px-2.5 py-1.5 rounded" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.18)", color: "#c5cad8", cursor: "pointer" }} data-testid="bi-fx-rates-year-next"><ChevronRight size={14} /></button>
        {isPlatformAdmin && (
          <>
            <button onClick={onCopyPrev} className="px-2.5 py-1.5 rounded inline-flex items-center gap-1 ml-2" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.18)", color: "#c5cad8", ...mono(10, undefined, 700, "0.4px"), cursor: "pointer", textTransform: "uppercase" }} data-testid="bi-fx-rates-copy-prev"><Copy size={11} /> Copy from {year - 1}</button>
            <button onClick={onSave} className="px-3 py-1.5 rounded inline-flex items-center gap-1 ml-auto" style={{ background: "#185FA5", border: "none", color: "#fff", ...mono(10, "#fff", 700, "0.4px"), cursor: "pointer", textTransform: "uppercase", opacity: bulk.isPending ? 0.7 : 1 }} disabled={bulk.isPending} data-testid="bi-fx-rates-save"><Save size={11} /> {bulk.isPending ? "Saving…" : "Save changes"}</button>
          </>
        )}
      </div>

      {isLoading && <div style={mono(11, "#94a3b8")}><Loader2 size={12} className="inline animate-spin mr-2" />Loading rates…</div>}

      <div className="rounded-lg overflow-x-auto" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }} data-testid="bi-fx-rates-grid">
        <table style={{ width: "100%", borderCollapse: "collapse", ...mono(11) }}>
          <thead>
            <tr style={{ background: "rgba(0,0,0,0.30)" }}>
              <th style={{ padding: "10px 12px", textAlign: "left", ...mono(9, "#94a3b8", 700, "0.5px"), textTransform: "uppercase", minWidth: 220 }}>Month</th>
              <th style={{ padding: "10px 12px", textAlign: "left", ...mono(9, "#94a3b8", 700, "0.5px"), textTransform: "uppercase" }}>EUR → HUF rate (HUF per 1 EUR)</th>
            </tr>
          </thead>
          <tbody>
            {MONTHS.map((label, idx) => {
              const m = idx + 1;
              const val = draft[m] ?? "";
              return (
                <tr key={m} data-testid={`bi-fx-rates-row-${m}`} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding: "8px 12px", ...mono(11, "#f0f2f8", 700) }}>{label} {year}</td>
                  <td style={{ padding: "6px 8px" }}>
                    <input
                      type="number"
                      step="0.0001"
                      value={val === null ? "" : val}
                      onChange={(e) => setDraft((p) => ({ ...p, [m]: e.target.value }))}
                      placeholder="e.g. 395.50"
                      disabled={!isPlatformAdmin}
                      style={{
                        background: "transparent",
                        border: "1px solid rgba(255,255,255,0.12)",
                        color: "#f0f2f8",
                        ...mono(12),
                        padding: "6px 10px",
                        borderRadius: 4,
                        width: 220,
                      }}
                      data-testid={`bi-fx-rates-input-m${m}`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-3" style={mono(10, "#94a3b8")}>
        Tip: leave a cell blank to keep "no rate". Months without a rate force HUF-currency deals to be excluded from EUR totals (and vice versa). The BI dashboard surfaces a warning chip when a slice contains months without a rate.
      </div>
    </div>
  );
}
